import React, { useState, useEffect } from 'react';
import { useOrder } from '../../context/OrderContext';
import { RALModal } from '../../modals';

// Helper to check if finish requires RAL code
const isSpecialFinish = (code) => code === 'V1' || code === 'V2';

// Handrail configuration rules - grouped by base type
const HANDRAIL_CONFIGS = {
    // Universal handrails - valid for all profiles
    universal: [

    ],
    // N6 specific handrails with glass thickness requirement
    n6: [
        {
            code: 'N6-P1*',
            baseCode: 'N6-P1*',
            name: 'N6-P1',
            image: '/images/accessories/CORRIMANO/SEL-N6-P1.png',
            finishFormat: 'standard',
            needsGlass: true,
            description: 'Valido per tutti - Richiede spessore vetro',
            supportsSupplyType: true,
            autoProducts: {
                '8+8': [{ code: 'N6-G2', quantity: 2 }],
                '10+10': [{ code: 'N6-G1', quantity: 1 }, { code: 'N6-G2', quantity: 1 }],
                '12+12': [{ code: 'N6-G1', quantity: 2 }]
            }
        },
        {
            code: 'N6-P2*',
            baseCode: 'N6-P2*',
            name: 'N6-P2',
            image: '/images/accessories/CORRIMANO/SEL-N6-P2.png',
            finishFormat: 'standard',
            needsGlass: true,
            description: 'Valido per tutti - Richiede spessore vetro',
            supportsSupplyType: true,
            autoProducts: {
                '8+8': [{ code: 'AD-015-N', quantity: 2 }]
            }
        },
        {
            code: 'N6-P3*',
            baseCode: 'N6-P3*',
            name: 'N6-P3',
            image: '/images/accessories/CORRIMANO/SEL-N6-P3.png',
            finishFormat: 'standard',
            needsGlass: true,
            description: 'Corrimano Minimal Grande - Vetro 10+10',
            supportsSupplyType: true,
            validGlass: ['10+10'],
            maxBarLength: 2999,
            supplyOptions: ['3m', 'misura']
            // No autoProducts - nessuna guarnizione
        },
        {
            code: 'N6-P4*',
            baseCode: 'N6-P4*',
            name: 'N6-P4',
            image: '/images/accessories/CORRIMANO/SEL-N6-P4.png',
            finishFormat: 'standard',
            needsGlass: true,
            description: 'Corrimano Minimal Piccolo - Vetro 8+8',
            supportsSupplyType: true,
            validGlass: ['8+8'],
            maxBarLength: 2999,
            supplyOptions: ['3m', 'misura']
            // No autoProducts - nessuna guarnizione
        }
    ],
    // Special accessories for CA-02
    ca02Accessories: [

    ],
    // Additional N6 accessories
    n6Accessories: [
        {
            code: 'N6-ANGOLARE-P1*',
            name: 'N6 Angolare P1',
            image: '/images/accessories/CORRIMANO/SEL-N6-P1-ANGO.png',
            parentProduct: 'N6-P1',
            finishFormat: 'limited',
            description: 'Valido per passamano N6-P1'
        },
        {
            code: 'N6-LINEARE-P1*',
            name: 'N6 Lineare P1',
            image: '/images/accessories/CORRIMANO/SEL-N6-P1-LINE.png',
            parentProduct: 'N6-P1',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P1'
        },
        {
            code: 'N6-LINEARE-P2*',
            name: 'N6 Lineare P2',
            image: '/images/accessories/CORRIMANO/SEL-N6-P2-LINE.png',
            parentProduct: 'N6-P2',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P2'
        },
        {
            code: 'N6-6P1*',
            name: 'N6-6P1',
            image: '/images/accessories/CORRIMANO/SEL-N6-6P1.png',
            parentProduct: 'N6-P1',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P1'
        },
        {
            code: 'N6-6P2*',
            name: 'N6-6P2',
            image: '/images/accessories/CORRIMANO/SEL-N6-6P2.png',
            parentProduct: 'N6-P2',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P2'
        },
        {
            code: 'N6-LINEARE-P3*',
            name: 'N6 Lineare P3',
            image: '/images/accessories/CORRIMANO/SEL-N6-P3-LINE.png',
            parentProduct: 'N6-P3',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P3'
        },
        {
            code: 'N6-LINEARE-P4*',
            name: 'N6 Lineare P4',
            image: '/images/accessories/CORRIMANO/SEL-N6-P4-LINE.png',
            parentProduct: 'N6-P4',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P4'
        },
        {
            code: 'N6-6P3*',
            name: 'N6-6P3',
            image: '/images/accessories/CORRIMANO/SEL-N6-6P3.png',
            parentProduct: 'N6-P3',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P3'
        },
        {
            code: 'N6-6P4*',
            name: 'N6-6P4',
            image: '/images/accessories/CORRIMANO/SEL-N6-6P4.png',
            parentProduct: 'N6-P4',
            finishFormat: 'standard',
            description: 'Valido per passamano N6-P4'
        }
    ]
};

// Available finishes based on format type
const FINISH_CONFIGS = {
    empty: [
        { code: '', name: 'Argento', suffix: '', color: '#C0C0C0' },
        { code: '9010', name: 'RAL9010 Opaco', suffix: '/9010', color: '#FFFFFF' },
        { code: '9005', name: 'RAL9005 Opaco', suffix: '/9005', color: '#000000' },
        { code: 'G', name: 'Grezzo', suffix: '/G', color: '#E0E0E0' },
        { code: 'V1', name: 'Verniciato Speciale 1', suffix: '/V1', color: '#D3D3D3' },
        { code: 'V2', name: 'Verniciato Speciale 2', suffix: '/V2', color: '#B8B8B8' }
    ],
    standard: [
        { code: 'A', name: 'Argento', suffix: 'A', color: '#C0C0C0' },
        { code: 'B', name: 'RAL9010 Opaco', suffix: 'B', color: '#FFFFFF' },
        { code: 'N', name: 'RAL9005 Opaco', suffix: 'N', color: '#000000' },
        { code: 'G', name: 'Grezzo', suffix: 'G', color: '#E0E0E0' },
        { code: 'V1', name: 'Verniciato Speciale 1', suffix: 'V1', color: '#D3D3D3' },
        { code: 'V2', name: 'Verniciato Speciale 2', suffix: 'V2', color: '#B8B8B8' }
    ],
    ca02: [
        { code: 'N', name: 'Argento', suffix: 'N', color: '#C0C0C0' },
        { code: '9010', name: 'RAL9010 Opaco', suffix: '9010', color: '#FFFFFF' },
        { code: '9005', name: 'RAL9005 Opaco', suffix: '9005', color: '#000000' },
        { code: 'G', name: 'Grezzo', suffix: 'G', color: '#E0E0E0' },
        { code: 'V1', name: 'Verniciato Speciale 1', suffix: 'V1', color: '#D3D3D3' },
        { code: 'V2', name: 'Verniciato Speciale 2', suffix: 'V2', color: '#B8B8B8' }
    ],
    ca02_accessory: [
        { code: 'N', name: 'Argento', suffix: '-N', color: '#C0C0C0' },
        { code: '9010', name: 'RAL9010 Opaco', suffix: '-9010', color: '#FFFFFF' },
        { code: '9005', name: 'RAL9005 Opaco', suffix: '-9005', color: '#000000' },
        { code: 'G', name: 'Grezzo', suffix: '-G', color: '#E0E0E0' },
        { code: 'V1', name: 'Verniciato Speciale 1', suffix: '-V1', color: '#D3D3D3' },
        { code: 'V2', name: 'Verniciato Speciale 2', suffix: '-V2', color: '#B8B8B8' }
    ],
    limited: [
        { code: 'A', name: 'Argento', suffix: 'A', color: '#C0C0C0' },
        { code: 'G', name: 'Grezzo', suffix: 'G', color: '#E0E0E0' }
    ]
};

// Glass thickness options for N6 handrails
const GLASS_OPTIONS = [
    { value: '8+8', label: 'Vetro 8+8' },
    { value: '10+10', label: 'Vetro 10+10' },
    { value: '12+12', label: 'Vetro 12+12' }
];

// Supply type options
const SUPPLY_TYPE_OPTIONS = [
    { value: '3m', label: 'Barre da 3 Metri', icon: '3M' },
    { value: '6m', label: 'Barre da 6 Metri', icon: '6M' },
    { value: 'misura', label: 'Tagliato a Misura', icon: 'M' }
];

export const HandrailSelector = ({ onSelect, initialValues = {}, isEditMode = false, profileType: propProfileType }) => {
    const { state } = useOrder();
    // Use profileType from props (passed from navigation) or fall back to order state
    const profileType = propProfileType || state?.orderDetails?.profileType || state?.orderDetails?.ninfaNumber;

    // Normalize profile type to ensure proper format (e.g., "6" -> "N6", "6S" -> "N6")
    const rawProfile = profileType ? profileType.toString() : null;
    const normalizedProfileType = rawProfile
        ? (rawProfile === '6S' ? 'N6' : (rawProfile.startsWith('N') ? rawProfile : `N${rawProfile}`))
        : null;

    // State management
    const [step, setStep] = useState(1);
    const [selectedHandrails, setSelectedHandrails] = useState(initialValues?.selectedHandrails || []);
    const [supplyType, setSupplyType] = useState(initialValues?.supplyType || '');
    const [finish, setFinish] = useState(initialValues?.finish || '');
    const [ralCode, setRalCode] = useState(initialValues?.ralCode || null);
    const [glassThickness, setGlassThickness] = useState(initialValues?.glassThickness || '');
    const [selectedProfileForAccessory, setSelectedProfileForAccessory] = useState(normalizedProfileType || '');
    const [customBars, setCustomBars] = useState(initialValues?.customBars || []);

    // RAL Modal state
    const [showRALModal, setShowRALModal] = useState(false);
    const [pendingFinishCode, setPendingFinishCode] = useState(null);

    const isAccessoryOnlyFlow = !normalizedProfileType; // True when ordering accessories without selecting profile first

    // Reset when profile changes
    useEffect(() => {
        if (profileType && profileType !== selectedProfileForAccessory) {
            setSelectedProfileForAccessory(profileType);
            setStep(1);
            setSelectedHandrails([]);
            setSupplyType('');
            setFinish('');
            setGlassThickness('');
        }
    }, [profileType, selectedProfileForAccessory]);

    // Get available handrails filtered by profile type
    const getAvailableHandrails = () => {
        const allHandrails = [];

        // Always add universal handrails (compatible with all profiles)
        HANDRAIL_CONFIGS.universal.forEach(handrail => {
            allHandrails.push({ ...handrail, category: 'Universale' });
        });

        // If we have a profile type, filter by that specific profile
        if (normalizedProfileType) {
            // Check if this is an N6 profile
            if (normalizedProfileType === 'N6') {
                // Add N6 specific handrails
                HANDRAIL_CONFIGS.n6.forEach(handrail => {
                    allHandrails.push({ ...handrail, category: 'n6' });
                });

                // Add N6 accessories
                HANDRAIL_CONFIGS.n6Accessories.forEach(handrail => {
                    allHandrails.push({ ...handrail, category: 'n6Accessories' });
                });
            }
            // Add more profile types here as needed (N7, N8, etc.)
            // else if (normalizedProfileType === 'N7') { ... }
        } else if (isAccessoryOnlyFlow) {
            // When no profile is selected (standalone accessories order)
            // Show all available handrails
            HANDRAIL_CONFIGS.n6.forEach(handrail => {
                allHandrails.push({ ...handrail, category: 'n6' });
            });

            HANDRAIL_CONFIGS.n6Accessories.forEach(handrail => {
                allHandrails.push({ ...handrail, category: 'n6Accessories' });
            });

            HANDRAIL_CONFIGS.ca02Accessories.forEach(handrail => {
                allHandrails.push({ ...handrail, category: 'ca02Accessories' });
            });
        }

        return allHandrails;
    };

    const availableHandrails = getAvailableHandrails();

    // Get available supply type options based on selected handrails
    const getAvailableSupplyOptions = () => {
        const handrailsWithOptions = selectedHandrails.filter(hr => hr.supplyOptions);
        if (handrailsWithOptions.length === 0) return SUPPLY_TYPE_OPTIONS;
        // Get intersection of allowed supply options
        const allowedValues = handrailsWithOptions.reduce((acc, hr) => {
            return acc.filter(val => hr.supplyOptions.includes(val));
        }, SUPPLY_TYPE_OPTIONS.map(o => o.value));
        return SUPPLY_TYPE_OPTIONS.filter(o => allowedValues.includes(o.value));
    };

    // Get max bar length for custom cut based on selected handrails
    const getMaxBarLength = () => {
        const handrailsWithMax = selectedHandrails.filter(hr => hr.maxBarLength);
        if (handrailsWithMax.length === 0) return 5999;
        return Math.min(...handrailsWithMax.map(hr => hr.maxBarLength));
    };

    // Get available glass options based on selected handrails
    const getAvailableGlassOptions = () => {
        const handrailsWithGlass = selectedHandrails.filter(hr => hr.validGlass);
        if (handrailsWithGlass.length === 0) return GLASS_OPTIONS;
        const validGlasses = new Set();
        handrailsWithGlass.forEach(hr => hr.validGlass.forEach(g => validGlasses.add(g)));
        return GLASS_OPTIONS.filter(o => validGlasses.has(o.value));
    };

    const handleHandrailSelection = (handrailData) => {
        const handrailCode = handrailData.code;

        setSelectedHandrails(prev => {
            const existing = prev.find(hr => hr.code === handrailCode);
            if (existing) {
                return prev.filter(hr => hr.code !== handrailCode);
            } else {
                // Get default finish based on finishFormat
                const defaultFinish = handrailData.finishFormat === 'empty' ? '' :
                    handrailData.finishFormat === 'ca02' || handrailData.finishFormat === 'ca02_accessory' ? 'N' : 'A';
                return [...prev, {
                    ...handrailData,
                    quantity: 1,
                    finish: defaultFinish, // Individual finish for each handrail
                    ralCode: null // Individual RAL code for each handrail
                }];
            }
        });
    };

    const updateHandrailQuantity = (handrailCode, quantity) => {
        setSelectedHandrails(prev =>
            prev.map(hr =>
                hr.code === handrailCode ? { ...hr, quantity: quantity === '' ? '' : Math.max(1, quantity) } : hr
            )
        );
    };

    const updateHandrailSupplyType = (handrailCode, supplyValue) => {
        setSelectedHandrails(prev =>
            prev.map(hr =>
                hr.code === handrailCode ? { ...hr, supplyType: supplyValue } : hr
            )
        );
    };

    const updateHandrailGlass = (handrailCode, glassValue) => {
        setSelectedHandrails(prev =>
            prev.map(hr =>
                hr.code === handrailCode ? { ...hr, glassThickness: glassValue } : hr
            )
        );
    };

    // Track which handrail is being updated for RAL modal
    const [pendingHandrailCode, setPendingHandrailCode] = useState(null);

    const updateHandrailFinish = (handrailCode, finishCode) => {
        if (isSpecialFinish(finishCode)) {
            // Show RAL modal for V1/V2
            setPendingFinishCode(finishCode);
            setPendingHandrailCode(handrailCode);
            setShowRALModal(true);
        } else {
            // Normal finish selection
            setSelectedHandrails(prev =>
                prev.map(hr =>
                    hr.code === handrailCode ? { ...hr, finish: finishCode, ralCode: null } : hr
                )
            );
        }
    };

    // Handle RAL confirmation for handrails
    const handleRALConfirm = (ralCode) => {
        setShowRALModal(false);
        if (pendingHandrailCode && pendingFinishCode) {
            setSelectedHandrails(prev =>
                prev.map(hr =>
                    hr.code === pendingHandrailCode ? { ...hr, finish: pendingFinishCode, ralCode } : hr
                )
            );
        }
        setPendingFinishCode(null);
        setPendingHandrailCode(null);
    };

    const handleSupplyTypeSelect = (supplyTypeValue) => {
        setSupplyType(supplyTypeValue);

        if (supplyTypeValue === 'misura') {
            setStep(6); // Go to custom bars input
        } else {
            // Always route through the per-row quantity/color step (works for 1+ handrails).
            // The old single-handrail "global finish" page (step 3) didn't persist the color
            // correctly, so we skip it entirely.
            const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
            setStep(needsGlass ? 4 : 5);
        }
    };

    const handleFinishSelect = (finishCode) => {
        if (isSpecialFinish(finishCode)) {
            // Show RAL modal for V1/V2
            setPendingFinishCode(finishCode);
            setShowRALModal(true);
        } else {
            setFinish(finishCode);
            setRalCode(null);
            const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
            setStep(needsGlass ? 4 : 5); // Skip glass step if not needed
        }
    };

    // Handle RAL confirmation for global finish (single handrail case)
    const handleRALConfirmGlobal = (code) => {
        setShowRALModal(false);
        setFinish(pendingFinishCode);
        setRalCode(code);
        setPendingFinishCode(null);
        const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
        setStep(needsGlass ? 4 : 5);
    };

    // Determine which RAL confirm handler to use
    const handleRALConfirmWrapper = (code) => {
        if (pendingHandrailCode) {
            // Individual handrail finish
            handleRALConfirm(code);
        } else {
            // Global finish (single handrail)
            handleRALConfirmGlobal(code);
        }
    };

    // Handle RAL modal close
    const handleRALClose = () => {
        setShowRALModal(false);
        setPendingFinishCode(null);
        setPendingHandrailCode(null);
    };

    const handleGlassSelect = (thickness) => {
        setGlassThickness(thickness);
        setStep(5); // Go to quantity configuration
    };

    const generateProductCodes = () => {
        const allProducts = [];

        selectedHandrails.forEach(handrail => {
            // Use individual finish for each handrail, fallback to global finish for backward compatibility
            const handrailFinish = handrail.finish !== undefined ? handrail.finish : finish;
            const handrailRalCode = handrail.ralCode !== undefined ? handrail.ralCode : ralCode;

            // Generate main product code based on supply type
            const availableFinishesForHandrail = FINISH_CONFIGS[handrail.finishFormat] || FINISH_CONFIGS.standard;
            const finishConfig = availableFinishesForHandrail.find(f => f.code === handrailFinish);

            // Use baseCode if available, otherwise use code (for accessories)
            const codeToProcess = handrail.baseCode || handrail.code;
            if (!codeToProcess) {
                console.warn('Handrail missing both baseCode and code:', handrail);
                return; // Skip this handrail if it has neither baseCode nor code
            }
            let finalCode = codeToProcess.replace('*', finishConfig?.suffix || '');

            // Per-row supply type (falls back to global for accessories that don't choose one)
            const rowSupply = handrail.supplyType || supplyType;

            // Add L suffix for misura (tagliato a misura)
            if (rowSupply === 'misura' && handrail.supportsSupplyType) {
                finalCode = finalCode + 'L';
            }

            // Calculate quantity in METERS based on supply type
            let finalQuantity = handrail.quantity;
            let supplyTypeLabel = '';

            if (rowSupply === '3m') {
                supplyTypeLabel = 'Barre da 3 Metri';
                // quantity × 3 meters per bar
                finalQuantity = handrail.quantity * 3;
            } else if (rowSupply === '6m') {
                supplyTypeLabel = 'Barre da 6 Metri';
                // quantity × 6 meters per bar
                finalQuantity = handrail.quantity * 6;
            } else if (rowSupply === 'misura') {
                supplyTypeLabel = 'Tagliato a Misura';
                // Calculate total millimeters from custom bars, then convert to meters with 3 decimals
                if (customBars.length > 0) {
                    const totalMm = customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                    finalQuantity = parseFloat((totalMm / 1000).toFixed(3));
                }
            }

            const ralInfo = handrailRalCode ? ` - ${handrailRalCode}` : '';
            const mainProduct = {
                code: finalCode,
                quantity: finalQuantity,
                description: `${handrail.name} - ${supplyTypeLabel} - ${finishConfig?.name || handrailFinish || 'Nessuno'}${ralInfo}${isAccessoryOnlyFlow ? ' (Universal)' : ''}`,
                category: 'main',
                ralCode: handrailRalCode || null
            };

            allProducts.push(mainProduct);

            // Add automatic products for N6 handrails (use per-row glass when set)
            const rowGlass = handrail.glassThickness || glassThickness;
            if (handrail.needsGlass && handrail.autoProducts && rowGlass) {
                const autoProductsForGlass = handrail.autoProducts[rowGlass];
                if (autoProductsForGlass) {
                    autoProductsForGlass.forEach(autoProduct => {
                        allProducts.push({
                            code: autoProduct.code,
                            quantity: autoProduct.quantity * finalQuantity,
                            description: `${autoProduct.code} (Aggiunta automatica per ${rowGlass})`,
                            category: 'automatic'
                        });
                    });
                }
            }
        });

        return allProducts;
    };

    const handleSubmit = () => {
        const productCodes = generateProductCodes();
        const handrailConfig = {
            selectedHandrails,
            supplyType,
            finish,
            ralCode: ralCode || null,
            glassThickness,
            customBars: supplyType === 'misura' ? customBars : undefined,
            productCodes,
            profileType: profileType || selectedProfileForAccessory
        };

        onSelect(handrailConfig);
    };

    // Get available finishes for selected handrails
    const getAvailableFinishes = () => {
        if (selectedHandrails.length === 0) return [];

        // Get the most restrictive finish format
        const finishFormats = selectedHandrails.map(hr => hr.finishFormat);
        if (finishFormats.includes('limited')) return FINISH_CONFIGS.limited;
        if (finishFormats.includes('empty')) return FINISH_CONFIGS.empty;
        if (finishFormats.includes('ca02')) return FINISH_CONFIGS.ca02;
        if (finishFormats.includes('ca02_accessory')) return FINISH_CONFIGS.ca02_accessory;
        return FINISH_CONFIGS.standard;
    };

    // Render appropriate step
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">
                            {isAccessoryOnlyFlow ? 'Seleziona i Corrimano' : 'Seleziona i Corrimano'}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {isAccessoryOnlyFlow ? 'Scegli i corrimano per qualsiasi utilizzo' : 'Scegli i corrimano per il tuo progetto'}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {availableHandrails.map((handrailData) => {
                                const isSelected = selectedHandrails.some(hr => hr.code === handrailData.code);

                                return (
                                    <button
                                        key={handrailData.code}
                                        onClick={() => handleHandrailSelection(handrailData)}
                                        className={`p-4 border-2 rounded-xl transition-all ${isSelected ? 'bg-red-100 border-red-500' : 'border-gray-200 bg-white hover:border-red-500 hover:shadow-lg'}`}
                                    >
                                        <div className="flex flex-col items-center">
                                            {handrailData.image ? (
                                                <div className="w-full h-20 mb-2 flex items-center justify-center">
                                                    <img
                                                        src={handrailData.image}
                                                        alt={handrailData.name}
                                                        className="h-16 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextElementSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-16 h-16 hidden items-center justify-center bg-red-50 rounded-full">
                                                        <span className="text-lg font-bold">
                                                            {handrailData.supportsSupplyType ? 'H' : 'A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="w-16 h-16 flex items-center justify-center mb-2 bg-red-50 rounded-full">
                                                    <span className="text-lg font-bold">
                                                        {handrailData.supportsSupplyType ? 'H' : 'A'}
                                                    </span>
                                                </div>
                                            )}
                                            <span className="font-medium text-gray-700">{handrailData.name}</span>
                                            <span className="text-sm text-gray-500">{handrailData.code.replace('*', 'X')}</span>
                                            {handrailData.needsGlass && (
                                                <span className="text-xs text-orange-600 mt-1">Richiede vetro</span>
                                            )}
                                            {isAccessoryOnlyFlow && (
                                                <span className="text-xs text-red-600 mt-1">{handrailData.category}</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6">
                            <button
                                onClick={() => {
                                    if (selectedHandrails.length === 0) return;
                                    // If no selected item supports supply type (e.g. only accessories/pezzi),
                                    // skip step 2 and go straight to glass (if needed) or quantity/color.
                                    const anySupports = selectedHandrails.some(hr => hr.supportsSupplyType);
                                    if (anySupports) {
                                        setStep(2);
                                    } else {
                                        setSupplyType('barre'); // default for pezzi
                                        const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
                                        setStep(needsGlass ? 4 : 5);
                                    }
                                }}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                disabled={selectedHandrails.length === 0}
                            >
                                Continua ({selectedHandrails.length} selezionati)
                            </button>
                        </div>
                    </div>
                );
            case 2: {
                // Per-row supply type UI (same horizontal layout as the color picker).
                // Only items with supportsSupplyType are shown; accessories (pezzi) skip this step.
                const supplyEligible = selectedHandrails.filter(hr => hr.supportsSupplyType);
                const getOptionsForHandrail = (hr) => {
                    if (!hr.supplyOptions) return SUPPLY_TYPE_OPTIONS;
                    // 'barre' in supplyOptions means both 3m and 6m are allowed; otherwise filter literally
                    const allowed = hr.supplyOptions.includes('barre')
                        ? SUPPLY_TYPE_OPTIONS.map(o => o.value)
                        : hr.supplyOptions;
                    return SUPPLY_TYPE_OPTIONS.filter(o => allowed.includes(o.value));
                };

                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Seleziona il Tipo di Fornitura</h2>
                        <p className="text-gray-600 mb-6">Per ogni corrimano selezionato, scegli il tipo di fornitura</p>

                        <div className="max-w-4xl mx-auto space-y-6 mb-6">
                            {supplyEligible.map((handrail) => {
                                const options = getOptionsForHandrail(handrail);
                                const current = handrail.supplyType || '';
                                return (
                                    <div key={handrail.code} className="p-6 border rounded-lg border-gray-200 bg-white">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                            <div className="text-left">
                                                <h3 className="font-medium text-lg mb-1">{handrail.name}</h3>
                                                <p className="text-sm text-gray-500">Codice base: {handrail.baseCode || handrail.code}</p>
                                                {current && (
                                                    <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                        {SUPPLY_TYPE_OPTIONS.find(o => o.value === current)?.label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="lg:col-span-2">
                                                <label className="block text-sm font-medium mb-3">Tipo di Fornitura:</label>
                                                <div className="grid grid-cols-3 gap-3">
                                                    {options.map(opt => (
                                                        <button
                                                            key={opt.value}
                                                            onClick={() => updateHandrailSupplyType(handrail.code, opt.value)}
                                                            className={`p-3 border rounded-lg transition-all ${current === opt.value
                                                                ? 'bg-red-100 border-red-500 ring-2 ring-red-300'
                                                                : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                        >
                                                            <div className="flex flex-col items-center">
                                                                <div className="w-10 h-10 flex items-center justify-center mb-1 bg-red-50 rounded-full">
                                                                    <span className="text-sm font-bold">{opt.icon}</span>
                                                                </div>
                                                                <span className="text-xs font-medium">{opt.label}</span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={() => {
                                    // Sync global supplyType for downstream steps:
                                    // - 'misura' if any item is misura (drives step 6)
                                    // - else first non-empty supplyType found
                                    const anyMisura = supplyEligible.some(h => h.supplyType === 'misura');
                                    const firstBar = supplyEligible.find(h => h.supplyType && h.supplyType !== 'misura');
                                    const globalSupply = anyMisura ? 'misura' : (firstBar ? firstBar.supplyType : 'barre');
                                    setSupplyType(globalSupply);
                                    const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
                                    if (globalSupply === 'misura') setStep(6);
                                    else setStep(needsGlass ? 4 : 5);
                                }}
                                disabled={supplyEligible.some(h => !h.supplyType)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continua
                            </button>
                        </div>
                    </div>
                );
            }
            // case 3 (global finish picker) removed: finish is always chosen per-row in step 5.
            case 4: {
                // Per-row glass thickness UI (same horizontal layout as colors and supply type).
                const glassEligible = selectedHandrails.filter(hr => hr.needsGlass);
                const getGlassForHandrail = (hr) => {
                    if (!hr.validGlass) return GLASS_OPTIONS;
                    return GLASS_OPTIONS.filter(o => hr.validGlass.includes(o.value));
                };

                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Seleziona lo Spessore Vetro</h2>
                        <p className="text-gray-600 mb-6">Per ogni corrimano, scegli lo spessore del vetro</p>

                        <div className="max-w-4xl mx-auto space-y-6 mb-6">
                            {glassEligible.map((handrail) => {
                                const options = getGlassForHandrail(handrail);
                                const current = handrail.glassThickness || '';
                                return (
                                    <div key={handrail.code} className="p-6 border rounded-lg border-gray-200 bg-white">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                            <div className="text-left">
                                                <h3 className="font-medium text-lg mb-1">{handrail.name}</h3>
                                                <p className="text-sm text-gray-500">Codice base: {handrail.baseCode || handrail.code}</p>
                                                {current && (
                                                    <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                        Vetro {current}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="lg:col-span-2">
                                                <label className="block text-sm font-medium mb-3">Spessore Vetro:</label>
                                                <div className="grid grid-cols-3 gap-3">
                                                    {options.map(opt => (
                                                        <button
                                                            key={opt.value}
                                                            onClick={() => updateHandrailGlass(handrail.code, opt.value)}
                                                            className={`p-3 border rounded-lg transition-all ${current === opt.value
                                                                ? 'bg-red-100 border-red-500 ring-2 ring-red-300'
                                                                : 'border-gray-300 hover:border-gray-400'
                                                                }`}
                                                        >
                                                            <div className="flex flex-col items-center">
                                                                <div className="w-10 h-10 flex items-center justify-center mb-1 bg-red-50 rounded-full">
                                                                    <span className="text-sm font-bold">{opt.value}</span>
                                                                </div>
                                                                <span className="text-xs font-medium">{opt.label}</span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => {
                                    const anySupports = selectedHandrails.some(hr => hr.supportsSupplyType);
                                    setStep(anySupports ? 2 : 1);
                                }}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={() => {
                                    // Sync global glassThickness for backward compat (first non-empty)
                                    const first = glassEligible.find(h => h.glassThickness);
                                    if (first) setGlassThickness(first.glassThickness);
                                    setStep(5);
                                }}
                                disabled={glassEligible.some(h => !h.glassThickness)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continua
                            </button>
                        </div>
                    </div>
                );
            }
            case 5:
                // If multiple handrails, show quantity and color selection for each
                // Always show per-row color selection (also for a single handrail).
                const showColorSelection = selectedHandrails.length >= 1;

                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">
                            {showColorSelection ? 'Configura Quantità e Colori' : 'Configura le Quantità'}
                        </h2>
                        {showColorSelection && (
                            <p className="text-gray-600 mb-6">Per ogni corrimano selezionato, scegli la quantità e il colore</p>
                        )}

                        <div className={`max-w-${showColorSelection ? '4xl' : '2xl'} mx-auto space-y-${showColorSelection ? '6' : '4'} mb-6`}>
                            {selectedHandrails.map((handrail) => {
                                // Get available finishes for this specific handrail
                                const handrailFinishFormat = handrail.finishFormat;
                                const availableFinishesForHandrail = FINISH_CONFIGS[handrailFinishFormat] || FINISH_CONFIGS.standard;
                                const handrailFinish = handrail.finish || finish; // Use individual finish or fallback to global

                                return (
                                    <div key={handrail.code} className={`p-${showColorSelection ? '6' : '4'} border rounded-lg border-gray-200 bg-white`}>
                                        {showColorSelection ? (
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                                {/* Handrail Info */}
                                                <div className="text-left">
                                                    <h3 className="font-medium text-lg mb-2">
                                                        {handrail.name}
                                                        {isAccessoryOnlyFlow && (
                                                            <span className="text-red-600 ml-2">({handrail.category})</span>
                                                        )}
                                                    </h3>
                                                    <p className="text-sm text-gray-500">
                                                        Codice: {handrail.baseCode?.replace('*', availableFinishesForHandrail.find(f => f.code === handrailFinish)?.suffix || '') || handrail.code}{supplyType === 'misura' ? 'L' : ''}
                                                    </p>
                                                    {(() => {
                                                        const rowSupply = handrail.supplyType || supplyType;
                                                        if (handrail.supportsSupplyType) {
                                                            const label = rowSupply === 'misura'
                                                                ? 'Tagliato a misura'
                                                                : rowSupply === '6m' ? 'Barra 6000mm'
                                                                : rowSupply === '3m' ? 'Barra 3000mm'
                                                                : `Barra ${handrail.maxBarLength || 3000}mm`;
                                                            return (
                                                                <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                                                    {label}
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                                                                Pezzo
                                                            </span>
                                                        );
                                                    })()}
                                                    <div className="flex items-center space-x-2 mt-3">
                                                        <label className="text-sm font-medium">Quantità:</label>
                                                        <input
                                                            type="number"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            min="1"
                                                            value={handrail.quantity ?? ''}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                updateHandrailQuantity(handrail.code, raw === '' ? '' : Math.max(1, parseInt(raw) || 1));
                                                            }}
                                                            className="w-24 p-3 text-base border rounded-md text-center"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Colore Selection */}
                                                <div className="lg:col-span-2">
                                                    <label className="block text-sm font-medium mb-3">Seleziona Colore:</label>
                                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                                                        {availableFinishesForHandrail.map((finishOption) => (
                                                            <button
                                                                key={finishOption.code}
                                                                onClick={() => updateHandrailFinish(handrail.code, finishOption.code)}
                                                                className={`p-3 border rounded-lg transition-all ${handrailFinish === finishOption.code
                                                                    ? 'bg-red-100 border-red-500 ring-2 ring-red-300'
                                                                    : 'border-gray-300 hover:border-gray-400'
                                                                    }`}
                                                            >
                                                                <div className="flex flex-col items-center">
                                                                    <div
                                                                        className="w-8 h-8 rounded-full mb-1 border border-gray-300"
                                                                        style={{ backgroundColor: finishOption.color }}
                                                                    ></div>
                                                                    <span className="text-xs font-medium">{finishOption.code || 'Default'}</span>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-2">
                                                        Selezionato: {availableFinishesForHandrail.find(f => f.code === handrailFinish)?.name || handrailFinish || 'Nessuno'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <div className="text-left flex-1">
                                                    <span className="font-medium">
                                                        {handrail.name}
                                                        {isAccessoryOnlyFlow && (
                                                            <span className="text-red-600 ml-2">({handrail.category})</span>
                                                        )}
                                                    </span>
                                                    <p className="text-sm text-gray-500">
                                                        Codice: {handrail.baseCode?.replace('*', availableFinishesForHandrail.find(f => f.code === handrailFinish)?.suffix || '') || handrail.code}{supplyType === 'misura' ? 'L' : ''}
                                                    </p>
                                                </div>
                                                <div className="flex items-center space-x-3 w-full sm:w-auto">
                                                    <label className="text-sm font-medium">Quantità:</label>
                                                    <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        min="1"
                                                        value={handrail.quantity ?? ''}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            updateHandrailQuantity(handrail.code, raw === '' ? '' : Math.max(1, parseInt(raw) || 1));
                                                        }}
                                                        className="flex-1 sm:flex-none sm:w-24 p-3 text-base border rounded-md text-center"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <h3 className="font-medium mb-2">Riepilogo Prodotti</h3>
                            {generateProductCodes().map((product, index) => (
                                <div key={index} className={`text-sm ${product.category === 'automatic' ? 'text-orange-600' : ''}`}>
                                    <span className="font-medium">{product.code}</span> -
                                    Qty: {product.quantity} - {product.description}
                                    {product.category === 'automatic' && (
                                        <span className="ml-2 text-xs bg-orange-100 px-2 py-1 rounded">Auto</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => {
                                    const needsGlass = selectedHandrails.some(hr => hr.needsGlass);
                                    const anySupports = selectedHandrails.some(hr => hr.supportsSupplyType);
                                    // Back logic:
                                    // - if there is a glass step in this flow, go to it (4)
                                    // - else if any item supports supply type, go to supply step (2)
                                    // - else back to product selection (1)
                                    if (needsGlass) {
                                        setStep(4);
                                    } else if (anySupports) {
                                        setStep(2);
                                    } else {
                                        setStep(1);
                                    }
                                }}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={selectedHandrails.some(h => !h.quantity || h.quantity === '' || parseInt(h.quantity) < 1 || h.finish === undefined || h.finish === null)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                );

            case 6:
                // Custom bars input for "Tagliato a Misura"
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Tagliato a Misura
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Aggiungi le barre di cui hai bisogno con il pulsante "+"
                        </p>
                        <div className="space-y-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="font-medium text-red-900">Esempio</h4>
                                </div>
                                <p className="text-sm text-red-800">
                                    3 barre da 2900mm, 5 barre da 5500mm, 1 barra da 1000mm
                                </p>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center mb-2">
                                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <h4 className="font-medium text-yellow-900">Restrizioni</h4>
                                </div>
                                <p className="text-sm text-yellow-800">
                                    {getMaxBarLength() < 5999
                                        ? `Non puoi inserire valori maggiori di ${getMaxBarLength()}mm`
                                        : 'Non puoi inserire esattamente 3000mm, 6000mm o valori maggiori di 6000mm'
                                    }
                                </p>
                            </div>

                            {/* Bar rows */}
                            <div className="space-y-3">
                                {customBars.map((bar, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max="100"
                                                value={bar.quantity === '' ? '' : (bar.quantity || '')}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...customBars];
                                                    newBars[index].quantity = raw === '' ? '' : Math.max(1, parseInt(raw) || 1);
                                                    setCustomBars(newBars);
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="N° barre"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">barre da</span>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max={getMaxBarLength()}
                                                value={bar.length || ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...customBars];
                                                    if (raw === '') {
                                                        newBars[index].length = '';
                                                        setCustomBars(newBars);
                                                        return;
                                                    }
                                                    const lengthValue = parseInt(raw) || 0;
                                                    const maxLength = getMaxBarLength();

                                                    // Validate: cannot exceed max bar length or be exactly 3000/6000
                                                    if (lengthValue > maxLength || lengthValue === 3000 || lengthValue === 6000) {
                                                        e.target.classList.add('border-red-500', 'bg-red-50');
                                                        setTimeout(() => {
                                                            e.target.classList.remove('border-red-500', 'bg-red-50');
                                                        }, 1000);
                                                        return;
                                                    }

                                                    newBars[index].length = lengthValue;
                                                    setCustomBars(newBars);
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="Millimetri"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">mm</span>
                                        <button
                                            onClick={() => {
                                                const newBars = customBars.filter((_, i) => i !== index);
                                                setCustomBars(newBars);
                                            }}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Rimuovi"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add bar button */}
                            <button
                                onClick={() => {
                                    const newBars = [...customBars, { quantity: 1, length: 0 }];
                                    setCustomBars(newBars);
                                }}
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Aggiungi Barra
                            </button>

                            {/* Summary */}
                            {customBars.length > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Riepilogo:</h4>
                                    <div className="space-y-1 text-sm text-gray-700">
                                        {customBars.map((bar, idx) => (
                                            <div key={idx}>
                                                • {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm
                                            </div>
                                        ))}
                                        <div className="pt-2 mt-2 border-t border-gray-300 font-medium">
                                            Totale: {customBars.reduce((sum, bar) => sum + (bar.quantity || 0), 0)} barre
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button
                                    onClick={() => setStep(2)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Indietro
                                </button>
                                <button
                                    onClick={() => {
                                        // Validate that we have at least one bar with valid data
                                        const maxLength = getMaxBarLength();
                                        const invalidBars = customBars.filter(bar =>
                                            bar.length > maxLength || bar.length === 3000 || bar.length === 6000
                                        );

                                        if (invalidBars.length > 0) {
                                            alert(maxLength < 5999
                                                ? `Errore: Non puoi inserire barre maggiori di ${maxLength}mm`
                                                : 'Errore: Non puoi inserire barre di esattamente 3000mm, 6000mm o maggiori di 6000mm');
                                            return;
                                        }

                                        const validBars = customBars.filter(bar => bar.quantity > 0 && bar.length > 0);
                                        if (validBars.length > 0) {
                                            setCustomBars(validBars);
                                            setStep(3); // Go to finish selection
                                        } else {
                                            alert('Aggiungi almeno una barra con quantità e lunghezza valide');
                                        }
                                    }}
                                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                    disabled={customBars.length === 0}
                                >
                                    Avanti
                                </button>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // If no profile is selected or profile not supported (and not in accessory-only flow)
    if (!isAccessoryOnlyFlow && (!profileType)) {
        return (
            <div className="text-center p-8">
                <h2 className="text-xl font-bold mb-4">Seleziona i Corrimano</h2>
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p>Corrimano disponibili per tutti i profili NINFA.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {renderStep()}
            <RALModal
                isOpen={showRALModal}
                onClose={handleRALClose}
                onConfirm={handleRALConfirmWrapper}
                finishType={pendingFinishCode}
            />
        </div>
    );
};

export default HandrailSelector;